<template>
  <div class="validation">
    <h1 v-translate>
      Verify your mobile phone number
    </h1>
    <div class="validation__container" v-show="status !== 500">
      <div v-show="status === 200">
        <p class="validation__text" v-translate>We texted you a code.</p>
        <p class="validation__text" v-translate>Please enter it below:</p>
      </div>
      <div>
        <p class="validation__text ruby-font" v-translate v-show="status === 422">
          The code you entered did not match. Please re-enter the code we texted you below:
        </p>
      </div>
      <LoadSpinner v-if="!user || loading" />
      <form v-if="user" class="form" @submit.prevent="verifyNumber">
        <md-field class="md-invalid">
          <md-input
            tabindex="1"
            v-model="validatedCode"
            name="validated-code"
            id="validated-code"
            required
            inputmode="numeric"
            pattern="[0-9]*"
            autocomplete="one-time-code"
          ></md-input>
        </md-field>
        <button
          :disabled="!validatedCode.length || verifying"
          class="button"
          @click.prevent="verifyNumber"
          v-translate
        >
          Verify
        </button>
        <LoadSpinner v-if="verifying" />
      </form>
      <div class="validation__footer">
        <p v-translate>Didn't get our text?</p>
        <a class="validation__footer__link" @click="sendCode" v-translate>Resend code</a>
        <router-link :to="{ name: 'MyProfile' }" class="validation__footer__link" v-translate
          >Edit your mobile phone number</router-link
        >
      </div>
    </div>
    <div class="validation__container" v-show="status === 500">
      <p class="validation__text ruby-font" v-translate v-show="!codeError">
        The code you entered did not match. Please verify that you entered the correct mobile phone
        number and try again later.
      </p>
      <p class="validation__text ruby-font" v-translate v-show="codeError">
        {{ errorMessage }}
      </p>
      <button class="button" @click="nextPage" v-translate>Continue</button>
    </div>
  </div>
</template>

<script>
import User from '@/services/users'
import Employee from '@/services/employees'
import LoadSpinner from '@/components/LoadSpinner'

export default {
  props: {
    routeName: {
      type: String,
      default: 'Home',
    },
    codeError: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "You have reached the code request limit. Please try again later."
    }
  },
  components: {
    LoadSpinner,
  },
  data() {
    return {
      status: 200,
      loading: false,
      user: null,
      validatedCode: '',
      verifying: false,
      count: 0,
      verified: false,
    }
  },
  async created() {
    if (this.codeError) {
      this.status = 500
    }
    this.loading = true
    this.userId = this.$store.state.user.id
    await Employee.api.retrieve(this.userId).then(res => {
      this.user = res
    })
    this.loading = false
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.user.validatedNumber && !this.verified) {
      await this.updateSmsForFailedVerification()
    }
    next()
  },
  methods: {
    nextPage() {
      this.$router.push({ name: this.routeName })
    },
    async verifyNumber() {
      this.verifying = true
      try {
        await User.api.twilioVerify({ employee: this.user, code: this.validatedCode })
        this.verified = true
        this.nextPage()
      } catch (err) {
        if (err.response) {
          this.status = err.response.status
        }
      }
      this.validatedCode = ''
      this.verifying = false
    },
    async updateSmsForFailedVerification() {
      this.user.enableSmsAlerts = false
      this.user.enableEmailAlerts = true
      try {
        const response = await Employee.api.update(this.user, [
          'enableSmsAlerts',
          'enableEmailAlerts',
        ])
        this.user = response
        this.$store.dispatch('updateUser', Employee.fromAPI(response))
        this.$store.commit('UPDATE_CURRENT_LANGUAGE', this.user.preferredLanguage)
      } catch {
        this.user.id = this.userId
      }
    },
    async sendCode() {
      try {
        await User.api.twilioChallenge(this.user)
        this.validatedCode = ''
      } catch (err) {
        if (err.response) {
          this.status = err.response.status
          this.codeError = true
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.validation {
  @include screen-container();

  &__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  &__text {
    text-align: center;
  }

  &__footer {
    margin-top: 1em;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__link {
      display: block;
      margin-top: 0.25em;
      cursor: pointer;
    }
  }
}

.form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.button {
  @include primary-button(var(--dark-brand-color));
  cursor: pointer;
  margin-top: 1em;
}
.ruby-font {
  color: $ruby;
}
</style>
